import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import LobbyPage from '@/app/pages/LobbyPage/LobbyPage';
import { Permissions } from '@/app/types';
import { useMeetingData, useUserData } from '@/app/queries/MeetingQuery/context';
import { formatDate } from '@/app/utils/date';
import { readCapturePermissionState } from '@/app/utils/permissions';
import { EVENTS, gtag } from '@/lib/gtag';
import SentryImportPromise from '@/sentry/importPromise';


interface ILobbyPageProps {
  onJoin(permissions: Permissions): void;
}

const NOT_SETTLED: Permissions = { audio: false, video: false };

// A prompt the customer declined, or a device the machine does not have, is
// not a defect of ours, and the checklist already offers the way back.
const REFUSED_OR_ABSENT = ['NotAllowedError', 'NotFoundError'];

function stopStream(stream?: MediaStream) {
  stream?.getTracks().forEach(track => track.stop());
}

function isRefusedOrAbsent(error: unknown) {
  const name = (error as { name?: unknown } | null)?.name;

  return typeof name === 'string' && REFUSED_OR_ABSENT.includes(name);
}

function eachRequested(requested: Permissions, verdict: boolean): Permissions {
  return {
    ...(requested.audio ? { audio: verdict } : {}),
    ...(requested.video ? { video: verdict } : {}),
  };
}

async function reportDeviceFailure(requested: Permissions, error: unknown) {
  const { default: Sentry } = await SentryImportPromise;

  Sentry.captureException(error, {
    tags: { operation: 'lobby-capture' },
    extra: {
      requested,
      microphonePermission: await readCapturePermissionState('microphone'),
      cameraPermission: await readCapturePermissionState('camera'),
    },
  });
}

export default function LobbyPageContainer({ onJoin }: ILobbyPageProps) {
  const [permissions, setPermissions] = useState(NOT_SETTLED);
  // False on first paint means "not settled yet", not "denied" — the warning
  // waits for the actual verdict so the resolved layout is the first one drawn.
  const [denied, setDenied] = useState(NOT_SETTLED);
  const [videoStream, setVideoStream] = useState<MediaStream | undefined>();
  const isReleased = useRef(false);

  const meetingData = useMeetingData();
  const { isClient } = useUserData();

  const lobbyPageData = useMemo(() => {
    const customer = meetingData.participants.find((participant) => !participant.is_host);
    const staff = meetingData.participants.find((participant) => participant.is_host);

    return {
      startTime: formatDate(meetingData.meeting_data.appointment_start),
      address: meetingData.meeting_data.house_address,
      customerName: customer?.user_name,
      staffName: staff?.user_name
    }
  }, [meetingData]);

  const requestPermissions = useCallback(async (requested: Permissions) => {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(requested);
    } catch (error) {
      setPermissions((prevState) => ({ ...prevState, ...eachRequested(requested, false) }));
      setDenied((prevState) => ({ ...prevState, ...eachRequested(requested, true) }));
      if (!isRefusedOrAbsent(error)) {
        reportDeviceFailure(requested, error).catch(() => undefined);
      }
      throw error;
    }

    if (isReleased.current) {
      stopStream(stream);
      return;
    }

    setPermissions((prevState) => ({
      ...prevState,
      audio: requested.audio ? Boolean(stream.getAudioTracks().length) : prevState.audio,
      video: requested.video ? Boolean(stream.getVideoTracks().length) : prevState.video,
    }));
    setDenied((prevState) => ({ ...prevState, ...eachRequested(requested, false) }));

    if (requested.video) {
      setVideoStream(stream);
      return;
    }

    // The lobby wants the microphone's verdict, not its audio: a capture left
    // open here still holds the device when Agora asks for it at join.
    stopStream(stream);
  }, []);

  const handleJoinCall = () => {
    gtag(EVENTS.JOIN_CHANNEL, permissions);
    onJoin(permissions);
  };

  // A refusal is expected and already visible in the checklist, which offers
  // the retry.
  useEffect(function requestPermissionsOnMount() {
    requestPermissions({ audio: true }).catch(() => undefined);
    requestPermissions({ video: true }).catch(() => undefined);
  }, [requestPermissions]);

  useEffect(function releasePreview() {
    return () => stopStream(videoStream);
  }, [videoStream]);

  useEffect(function markReleasedOnUnmount() {
    isReleased.current = false;

    return () => {
      isReleased.current = true;
    };
  }, []);

  return (
    <LobbyPage
      {...lobbyPageData}
      permissions={permissions}
      denied={denied}
      requestPermissions={requestPermissions}
      videoStream={videoStream}
      onJoin={handleJoinCall}
      isClient={isClient}
    />
  );
}
